/**
 * Fiş başlığı çözümleyicisi.
 *
 * Aynı şubede birden fazla marka çalışabildiği için fiş başlığı artık tek bir
 * ekran ayarından gelmiyor: siparişin ait olduğu entegrasyonun kendi başlığı
 * varsa o kullanılıyor, olmayan alanlar ekran ayarından tamamlanıyor.
 *
 * Bu okuma daha önce yedi modalda ve iki yazdırma handler'ında birebir
 * tekrarlanıyordu. Marka desteğini her birine ayrı eklemek bir yerin
 * unutulmasını garanti edeceği için tekrar önce buraya indirildi.
 *
 * local-service tarafı değişmez: yazıcı katmanı `companyInfo`, `headerType`,
 * `logoUrl` ve `receiptLabel` alanlarını zaten jenerik basıyor, markaya göre
 * değişen içeriği web hazırlayıp aynı alanlarla gönderiyor.
 */

import {
  BrandReceiptSettings,
  PlatformIntegration,
  findIntegrationForSource,
  resolveStoreLabel,
  StoreLabelSource,
} from '../../../../types/platformIntegration';

/** Yazdırma servislerine gönderilen başlık alanları. */
export interface ResolvedReceiptHeader {
  companyInfo?: {
    name: string;
    address: string;
    taxNumber: string;
  };
  headerType: 'text' | 'logo';
  logoUrl?: string;
  logoMaxWidthPx?: number;
  logoMaxHeightPx?: number;
  standardFooterNote?: string;
  thankYouMessage?: string;
}

/**
 * Başlık çözümlenirken siparişten okunan alanlar. Farklı platformların ham
 * sipariş nesneleri de (PackageOrder'a dönüştürülmeden) doğrudan geçirilebilsin
 * diye hepsi opsiyonel.
 */
export interface ReceiptHeaderOrderRef extends StoreLabelSource {
  source?: string | null;
}

/** Marka alanlarını taşıyan (dönüştürülmüş) sipariş. */
interface StampedOrderLike {
  platformIntegrationId?: number | null;
  storeName?: string | null;
  externalStoreId?: string | null;
}

/**
 * Ham platform sipariş nesnesinden başlık çözümlemesi için gereken alanları
 * toplar.
 *
 * Migros'ta dikkat: ham siparişteki `storeName` Migros'un kendi platform mağaza
 * adıdır, kullanıcının girdiği marka adı `integrationStoreName` alanından
 * gelir — bu yüzden ham `storeName` yalnızca Migros dışındaki platformlarda
 * okunur.
 */
export const buildReceiptOrderRef = (
  platform: string,
  stampedOrder?: StampedOrderLike | null,
  raw?: any,
): ReceiptHeaderOrderRef => {
  const isMigros = platform === 'migros_yemek';
  const rawStoreName = isMigros
    ? raw?.integrationStoreName ?? raw?.IntegrationStoreName
    : raw?.storeName ?? raw?.StoreName;

  return {
    platform,
    platformIntegrationId:
      stampedOrder?.platformIntegrationId ?? raw?.platformIntegrationId ?? raw?.PlatformIntegrationId ?? null,
    storeName: text(stampedOrder?.storeName) || text(rawStoreName) || null,
    restaurantId: raw?.restaurantId ?? raw?.RestaurantId ?? null,
    remoteId: raw?.remoteId ?? raw?.RemoteId ?? null,
    platformRestaurantId: raw?.platformRestaurantId ?? raw?.PlatformRestaurantId ?? null,
    storeId: stampedOrder?.externalStoreId ?? raw?.storeId ?? raw?.StoreId ?? null,
  };
};

/** Ekran ayarındaki fiş başlığı; alan adları marka ayarıyla birebir aynı. */
type ScreenReceiptSettings = BrandReceiptSettings | null | undefined;

interface ScreenDataLike {
  printingSettings?: {
    receiptDisplaySettings?: ScreenReceiptSettings;
  } | null;
  platformIntegrations?: PlatformIntegration[] | null;
  branchId?: string | null;
}

const text = (value?: string | null): string => (value ?? '').trim();

/** Markaya özel başlık JSON'u; bozuk kayıt yazdırmayı engellememeli. */
export const parseBrandReceiptSettings = (json?: string | null): BrandReceiptSettings | undefined => {
  const raw = text(json);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as BrandReceiptSettings) : undefined;
  } catch (error) {
    console.warn('[ReceiptHeader] Marka fiş başlığı JSON çözümlenemedi, ekran varsayılanına düşülüyor:', error);
    return undefined;
  }
};

/** İki ayardan ilk dolu olanı seçer; marka kısmi doldurulmuş olabilir. */
const pickText = (brand?: string, screen?: string): string | undefined => {
  const fromBrand = text(brand);
  if (fromBrand) return fromBrand;
  const fromScreen = text(screen);
  return fromScreen || undefined;
};

const pickNumber = (brand?: number, screen?: number): number | undefined => {
  if (typeof brand === 'number' && !Number.isNaN(brand)) return brand;
  if (typeof screen === 'number' && !Number.isNaN(screen)) return screen;
  return undefined;
};

/**
 * Siparişin fiş başlığını çözer.
 *
 * Öncelik: (1) siparişin markasına ait başlık, (2) ekranın varsayılan başlığı,
 * (3) ikisi de yoksa `companyInfo` boş bırakılır — local-service kendi
 * fallback'ini uygular, yani bugünkü davranış korunur.
 *
 * Birleştirme alan alan yapılır: kullanıcı markada yalnızca şirket adını
 * doldurup adresi ekran ayarından miras alabilir.
 *
 * QR, kiosk, e-ticaret ve telefon siparişlerinin markası olmadığı için
 * entegrasyon eşleşmesi bulunamaz ve doğrudan ekran varsayılanı döner.
 */
export const resolveReceiptHeader = (
  order: ReceiptHeaderOrderRef | null | undefined,
  screenData: ScreenDataLike | null | undefined,
  integrations?: PlatformIntegration[] | null,
): ResolvedReceiptHeader => {
  const screen = screenData?.printingSettings?.receiptDisplaySettings || undefined;
  const list = integrations || screenData?.platformIntegrations || [];

  const integration = findIntegrationForSource(order, list);
  const brand = parseBrandReceiptSettings(integration?.receiptSettingsJson);

  const companyName = pickText(brand?.companyName, screen?.companyName);
  const companyAddress = pickText(brand?.companyAddress, screen?.companyAddress);
  const taxNumber = pickText(brand?.taxNumber, screen?.taxNumber);
  const hasCompanyInfo = !!(companyName || companyAddress || taxNumber);

  // headerType marka ayarında yalnızca marka başlığı tanımlıysa anlamlı;
  // aksi halde ekran ayarı, o da yoksa metin başlığı (bugünkü varsayılan).
  const headerType = brand?.headerType || screen?.headerType || 'text';

  return {
    companyInfo: hasCompanyInfo
      ? {
          name: companyName || '',
          address: companyAddress || '',
          taxNumber: taxNumber || '',
        }
      : undefined,
    headerType,
    logoUrl: pickText(brand?.logoUrl, screen?.logoUrl),
    logoMaxWidthPx: pickNumber(brand?.logoMaxWidthPx, screen?.logoMaxWidthPx),
    logoMaxHeightPx: pickNumber(brand?.logoMaxHeightPx, screen?.logoMaxHeightPx),
    standardFooterNote: pickText(brand?.standardFooterNote, screen?.standardFooterNote),
    thankYouMessage: pickText(brand?.thankYouMessage, screen?.thankYouMessage),
  };
};

/**
 * Çözülmüş başlığı, HTML önizleme şablonlarının beklediği düz şekle çevirir.
 * Şablonlar `companyName`/`companyAddress` alan adlarıyla yazıldığı için
 * `companyInfo` nesnesi burada tekrar düzleştirilir.
 */
export const toDisplaySettings = (header: ResolvedReceiptHeader): BrandReceiptSettings => ({
  headerType: header.headerType,
  companyName: header.companyInfo?.name,
  companyAddress: header.companyInfo?.address,
  taxNumber: header.companyInfo?.taxNumber,
  logoUrl: header.logoUrl,
  logoMaxWidthPx: header.logoMaxWidthPx,
  logoMaxHeightPx: header.logoMaxHeightPx,
  standardFooterNote: header.standardFooterNote,
  thankYouMessage: header.thankYouMessage,
});

/**
 * Fiş etiketi: platform adının yanına marka adı eklenir.
 *
 * Operatör fişe bakınca hem platformu hem markayı görmeli. Marka adı
 * çözülemezse bugünkü sabit metin aynen korunur.
 *
 * Türkçe karakterler olduğu gibi bırakılır: yazıcı katmanı win1254 ile basıyor
 * ve tam Türkçe destekliyor. Marka adını ASCII'ye indirmek kullanıcının girdiği
 * ismi bozmak olurdu. Uzun etiketi satır genişliğine kırpma işini
 * printer-handler.js zaten yapıyor.
 */
export const resolveReceiptLabel = (
  baseLabel: string,
  order: ReceiptHeaderOrderRef | null | undefined,
  screenData: ScreenDataLike | null | undefined,
  integrations?: PlatformIntegration[] | null,
): string => {
  const list = integrations || screenData?.platformIntegrations || [];

  // Şube adı bilerek geçilmiyor: etikete şube adı yazmak marka adı değil,
  // yanıltıcı bir bilgi olurdu — marka çözülemezse sabit etiket kalmalı.
  const storeLabel = resolveStoreLabel(order, list, null);
  if (!storeLabel) return baseLabel;

  // Marka adı zaten etiketin içindeyse tekrar eklenmez.
  if (baseLabel.toLocaleUpperCase('tr-TR').includes(storeLabel.toLocaleUpperCase('tr-TR'))) {
    return baseLabel;
  }

  return `${baseLabel} - ${storeLabel}`;
};
